import { useState } from 'react';

import { CheckIcon, CopyIcon, ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { SlackIntegration } from '@/shared/types/integration';
import { CHANNELS_URL, getDocsUrl } from '@/util/constants';

interface InstallationSectionProps {
  integration: SlackIntegration | undefined;
}

export const InstallationSection = ({ integration }: InstallationSectionProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const callbackUrl = integration?.integration_id
    ? `${CHANNELS_URL}/v1/slack/${integration.integration_id}`
    : '';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(callbackUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 4000);
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="px-1.5 text-sm leading-none font-medium text-foreground-muted">
        {t('integrations.slack.installation')}
      </p>
      <Card className="py-0 shadow-none">
        <CardContent className="px-4 pt-4 pb-5">
          <div className="flex flex-col gap-2">
            <label className="text-sm leading-5 font-medium">
              {t('integrations.callback_url')}
            </label>
            <InputGroup size="md" className="w-full bg-background">
              <InputGroupInput
                value={callbackUrl}
                readOnly
                className="text-sm text-foreground-muted"
              />
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  size="icon-sm"
                  onClick={handleCopy}
                  aria-label={t('common.copy')}
                  className="min-w-0"
                >
                  {copied ? (
                    <CheckIcon className="text-foreground-muted" size={16} />
                  ) : (
                    <CopyIcon className="text-foreground-muted" size={16} />
                  )}
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
            <p className="text-sm leading-5 text-foreground-muted">
              {t('integrations.slack.callback_url_subtitle')}
            </p>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              variant="ghost-neutral"
              size="sm"
              className="gap-1"
              onClick={() => window.open(getDocsUrl('docs/integrations/slack'), '_blank')}
            >
              {t('integrations.slack.installation_guide')}
              <ExternalLinkIcon className="h-4 w-4 text-foreground-muted" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
